import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import {
    customDateRange,
    type DateBounds,
    type DatePeriod,
    type DateTimeRange,
    periodDateBounds,
    resolveDatePeriod,
} from '#components/shared/datePeriod.ts';
import type { OrderSummary } from '#domain/Order.ts';
import { containsOrderSearchTerm } from '#utils/orderSearch.ts';

import { matchesOrderTimeRange } from './orderFilters.ts';

export const useOrderListFilters = (orders: OrderSummary[], isOpen: boolean, showServiceOrders: boolean) => {
    const intl = useIntl();
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [types, setTypes] = useState<string[]>(['transport', 'service']);
    const [period, setPeriod] = useState<DatePeriod>('all');
    const [customDates, setCustomDates] = useState<DateBounds>();
    const [customRange, setCustomRange] = useState<DateTimeRange>();
    const [now, setNow] = useState(Date.now);
    const debouncedSearch = useMemo(() => debounce(setSearch, 300), []);

    const reset = useCallback(() => {
        debouncedSearch.cancel();
        setSearchInput('');
        setSearch('');
        setTypes(['transport', 'service']);
        setPeriod('all');
        setCustomDates(undefined);
        setCustomRange(undefined);
        setNow(Date.now());
    }, [debouncedSearch]);

    useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

    useEffect(() => {
        if (!isOpen) {
            reset();
        }
    }, [isOpen, reset]);

    const range = period === 'custom' ? customRange : resolveDatePeriod(period, now);
    const selectedDates = useMemo(
        () => (period === 'custom' ? customDates : periodDateBounds(period, now)),
        [period, customDates, now]
    );
    const setCustomDatesRange = (value?: DateBounds) => {
        const nextRange = customDateRange(value?.from, value?.to);
        if (!nextRange) {
            return;
        }
        setCustomDates(value);
        setCustomRange(nextRange);
        setPeriod('custom');
    };
    const visibleOrders = useMemo(
        () =>
            orders.filter(
                order =>
                    (showServiceOrders || order.type !== 'service') &&
                    types.includes(order.type ?? 'transport') &&
                    containsOrderSearchTerm(order, search, intl) &&
                    matchesOrderTimeRange(order, range)
            ),
        [orders, showServiceOrders, types, search, range?.start, range?.end, intl]
    );

    return {
        searchInput,
        setSearchInput: (value: string) => {
            setSearchInput(value);
            debouncedSearch(value);
        },
        types,
        setTypes,
        period,
        setPeriod: (nextPeriod: Exclude<DatePeriod, 'custom'>) => {
            setNow(Date.now());
            setPeriod(nextPeriod);
            setCustomDates(undefined);
            setCustomRange(undefined);
        },
        selectedDates,
        setCustomDatesRange,
        reset,
        visibleOrders,
    };
};
